import React, { useState, useEffect } from "react";
import {
  withStyles,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { formatDate } from "../../../../../../Utilities/Functions";
import { recordTypes, processActions } from "../../../../../../API/config";
import { styles } from "./styles";
import { useTranslation } from "react-i18next";

const ViewRecord = (props) => {
  const { classes, record } = props;
  const { t } = useTranslation();

  const [recordFields, setRecordFields] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [checkRequests, setCheckRequests] = useState({});
  const [scores, setScores] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [associations, setAssociations] = useState([]);

  useEffect(() => {
    const recordFields = [];
    const attributes = [];
    const checkRequests = {};
    const scores = {};
    const addresses = [];
    const associations = [];

    const addField = (oldValue, newValue, name, list) => {
      if (
        (oldValue !== null && oldValue !== undefined) ||
        (newValue !== null && newValue !== undefined)
      ) {
        list.push({
          name,
          oldValue,
          newValue,
        });
      }
    };

    const parseField = (oldValue, newValue, fieldName) => {
      let ov = null;
      if (
        oldValue &&
        oldValue[fieldName] !== null &&
        oldValue[fieldName] !== undefined
      ) {
        ov = oldValue[fieldName];
        delete oldValue[fieldName];
      }
      let nv = null;
      if (
        newValue &&
        newValue[fieldName] !== null &&
        newValue[fieldName] !== undefined
      ) {
        nv = newValue[fieldName];
        delete newValue[fieldName];
      }
      return [ov, nv];
    };

    const parseBoolean = (oldValue, newValue, fieldName) => {
      let ov = null;
      if (
        oldValue &&
        oldValue[fieldName] !== null &&
        oldValue[fieldName] !== undefined
      ) {
        ov = Boolean(oldValue[fieldName])
          ? t("labels.common.yes")
          : t("labels.common.no");
        delete oldValue[fieldName];
      }
      let nv = null;
      if (
        newValue &&
        newValue[fieldName] !== null &&
        newValue[fieldName] !== undefined
      ) {
        nv = Boolean(newValue[fieldName])
          ? t("labels.common.yes")
          : t("labels.common.no");
        delete newValue[fieldName];
      }
      return [ov, nv];
    };

    const parseDate = (oldValue, newValue, fieldName) => {
      let oldDate;
      if (oldValue && oldValue[fieldName]) {
        oldDate = formatDate(oldValue[fieldName], true);
        delete oldValue[fieldName];
      }
      let newDate;
      if (newValue && newValue[fieldName]) {
        newDate = formatDate(newValue[fieldName], true);
        delete newValue[fieldName];
      }
      return [oldDate, newDate];
    };

    const parseUser = (oldValue, newValue, nameFieldName, emailFieldName) => {
      let oldUser;
      if (oldValue && (oldValue[nameFieldName] || oldValue[emailFieldName])) {
        oldUser = `${oldValue[nameFieldName]}(${oldValue[emailFieldName]})`;
        delete oldValue[nameFieldName];
        delete oldValue[emailFieldName];
      }
      let newUser;
      if (newValue && (newValue[nameFieldName] || newValue[emailFieldName])) {
        newUser = `${newValue[nameFieldName]}(${newValue[emailFieldName]})`;
        delete newValue[nameFieldName];
        delete newValue[emailFieldName];
      }
      return [oldUser, newUser];
    };

    const getAttrValue = (attr) => {
      const { sourceValue, sourceUnit, userValue, userUnit } = attr || {};
      const su = sourceUnit ? `(${sourceUnit})` : "";
      const sv = `${
        sourceValue !== null && sourceValue !== undefined ? sourceValue : ""
      }${su}`;
      const uu = userUnit ? `(${userUnit})` : "";
      const uv = `${
        userValue !== null && userValue !== undefined ? userValue : ""
      }${uu}`;
      return [sv, uv];
    };

    const parseAttributes = (oldValues, newValues) => {
      for (let idx = 0; idx < (oldValues || []).length; idx++) {
        const oldAttr = oldValues[idx];
        const newAttrIdx = (newValues || []).findIndex((a) =>
          a.id ? a.id === oldAttr.id : a.name === oldAttr.name,
        );
        const newAttr = newAttrIdx > -1 ? newValues[newAttrIdx] : null;
        const [oldSourceValue, oldUserValue] = getAttrValue(oldAttr);
        const [newSourceValue, newUserValue] = getAttrValue(newAttr);
        attributes.push({
          name: oldAttr.name,
          oldSourceValue,
          oldUserValue,
          newSourceValue,
          newUserValue,
        });
        if (newAttrIdx > -1) {
          (newValues || []).splice(newAttrIdx, 1);
        }
      }
      for (let idx = 0; idx < (newValues || []).length; idx++) {
        const newAttr = newValues[idx];
        const [newSourceValue, newUserValue] = getAttrValue(newAttr);
        attributes.push({
          name: newAttr.name,
          oldSourceValue: "",
          oldUserValue: "",
          newSourceValue,
          newUserValue,
        });
      }
    };

    const parseCheckRequest = (oldValue, newValue) => {
      const checkType =
        (oldValue || {}).checkType || (newValue || {}).checkType;
      const fields = [];
      addField(...parseField(oldValue, newValue, "id"), "checkId", fields);
      addField(
        ...parseField(oldValue, newValue, "countryName"),
        "JLL Country",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "entityId"),
        "Record Id",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "recordName"),
        "Record Name",
        fields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "allocatedUserName",
          "allocatedUserEmail",
        ),
        "Allocated To",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "processFlowState"),
        "Workflow State",
        fields,
      );
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        fields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        fields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        fields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        fields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "cancelled"),
        "Is Cancelled",
        fields,
      );
      checkRequests[checkType] = fields;
    };

    const parseCheckRequests = (oldValue, newValue) => {
      if (Array.isArray(oldValue) || Array.isArray(newValue)) {
        (oldValue || []).forEach((ocr) => {
          const idx = (newValue || []).findIndex(
            (cr) => cr.checkType === ocr.checkType,
          );
          const ncr = idx > -1 ? newValue[idx] : null;
          parseCheckRequest(ocr, ncr);
          if (idx > -1) {
            (newValue || []).splice(idx, 1);
          }
        });
        (newValue || []).forEach((ncr) => {
          parseCheckRequest(null, ncr);
        });
      } else {
        parseCheckRequest(oldValue, newValue);
      }
    };

    const parseScore = (oldValue, newValue) => {
      const checkType =
        (oldValue || {}).checkType || (newValue || {}).checkType;
      const fields = [];
      addField(
        ...parseField(oldValue, newValue, "guidedScore"),
        "Guided Score",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "acceptedScore"),
        "Accepted Score",
        fields,
      );
      addField(...parseField(oldValue, newValue, "reason"), "Reason", fields);
      addField(
        ...parseField(oldValue, newValue, "comments"),
        "Comments",
        fields,
      );
      addField(
        ...parseDate(oldValue, newValue, "validFromDate"),
        "Valid From",
        fields,
      );
      addField(
        ...parseDate(oldValue, newValue, "expiryDate"),
        "Expiry Date",
        fields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "canWorkWith"),
        "Can Work With",
        fields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "finalized"),
        "Finalized",
        fields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "approved"),
        "Approved",
        fields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "rejected"),
        "Rejected",
        fields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "expired"),
        "Expired",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "approverComment"),
        "Approver Comment",
        fields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "completedByUserName",
          "completedByUserEmail",
        ),
        "Completed By",
        fields,
      );
      scores[checkType] = fields;
    };

    const parseScores = (oldValue, newValue) => {
      if (Array.isArray(oldValue) || Array.isArray(newValue)) {
        (oldValue || []).forEach((os) => {
          const idx = (newValue || []).findIndex(
            (s) => s.checkType === os.checkType,
          );
          const ns = idx > -1 ? newValue[idx] : null;
          parseScore(os, ns);
          if (idx > -1) {
            (newValue || []).splice(idx, 1);
          }
        });
        (newValue || []).forEach((ns) => {
          parseScore(null, ns);
        });
      } else {
        parseScore(oldValue, newValue);
      }
    };

    const parseAddress = (oldValue, newValue) => {
      const fields = [];
      addField(...parseField(oldValue, newValue, "id"), "Address Id", fields);
      addField(
        ...parseField(oldValue, newValue, "addressType"),
        "Address Type",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "addressLine1"),
        "Address Line 1",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "addressLine2"),
        "Address Line 2",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "addressLine3"),
        "Address Line 3",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "addressLine4"),
        "Address Line 4",
        fields,
      );
      addField(...parseField(oldValue, newValue, "city"), "City", fields);
      addField(...parseField(oldValue, newValue, "county"), "County", fields);
      addField(
        ...parseField(oldValue, newValue, "stateProvince"),
        "Atate/Province",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "postcode"),
        "Postcode",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "jllRegion"),
        "Region",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "countryName"),
        "Country",
        fields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "isResidential"),
        "Residential",
        fields,
      );
      addField(...parseField(oldValue, newValue, "source"), "Source", fields);
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        fields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        fields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        fields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        fields,
      );
      addresses.push(fields);
    };

    const parseAddresses = (oldValue, newValue) => {
      if (Array.isArray(oldValue) || Array.isArray(newValue)) {
        (oldValue || []).forEach((oa, idx) => {
          const na = newValue && newValue[0] ? newValue[0] : null;
          parseAddress(oa, na);
          if ((newValue || []).length > 0) {
            newValue.splice(0, 1);
          }
        });
        (newValue || []).forEach((na) => {
          parseAddress(null, na);
        });
      } else {
        parseAddress(oldValue, newValue);
      }
    };

    const parseEntity = (oldValue, newValue) => {
      addField(
        ...parseField(oldValue, newValue, "id"),
        "Record Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "urn"),
        "Record URN",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        recordFields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "confidential"),
        "Is Confidential",
        recordFields,
      );
      if (oldValue["attributes"] || newValue["attributes"]) {
        parseAttributes(oldValue["attributes"], newValue["attributes"]);
      }
      if (oldValue["checkRequests"] || newValue["checkRequests"]) {
        parseCheckRequests(
          oldValue["checkRequests"],
          newValue["checkRequests"],
        );
      }
      if (oldValue["scores"] || newValue["scores"]) {
        parseScores(oldValue["scores"], newValue["scores"]);
      }
      if (oldValue["addresses"] || newValue["addresses"]) {
        parseAddresses(oldValue["addresses"], newValue["addresses"]);
      }
    };

    const parseAssociation = (oldValue, newValue) => {
      const fields = [];
      addField(
        ...parseField(oldValue, newValue, "associationName"),
        "Association Name",
        fields,
      );
      addField(
        ...parseField(oldValue, newValue, "targetRecordName"),
        "Target Record Name",
        fields,
      );

      const oldAttributes = (oldValue || {}).attributes || [];
      const newAttributes = (newValue || {}).attributes || [];
      for (let idx = 0; idx < oldAttributes.length; idx++) {
        const oldAttr = oldAttributes[idx];
        const newAttrIdx = newAttributes.findIndex((a) => a.id === oldAttr.id);
        const newAttr = newAttrIdx > -1 ? newAttributes[newAttrIdx] : {};
        addField(oldAttr.value, newAttr.value, oldAttr.name, fields);
        if (newAttrIdx > -1) {
          newAttributes.splice(newAttrIdx, 1);
        }
      }
      for (let idx = 0; idx < newAttributes.length; idx++) {
        const newAttr = newAttributes[idx];
        addField(null, newAttr.value, newAttr.name, fields);
      }

      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        fields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        fields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        fields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        fields,
      );

      associations.push(fields);
    };

    const parseAssociations = (oldValue, newValue) => {
      if (Array.isArray(oldValue) || Array.isArray(newValue)) {
        (oldValue || []).forEach((oa) => {
          const idx = (newValue || []).findIndex(
            (a) =>
              a.associationId === oa.associationId &&
              a.targetEntityId === oa.targetEntityId,
          );
          const na = idx > -1 ? newValue[idx] : null;
          parseAssociation(oa, na);
          if (idx > -1) {
            (newValue || []).splice(idx, 1);
          }
        });
        (newValue || []).forEach((na) => {
          parseAssociation(null, na);
        });
      } else {
        parseAssociation(oldValue, newValue);
      }
    };

    const parseEntityDocument = (oldValue, newValue) => {
      addField(
        ...parseField(oldValue, newValue, "id"),
        "Document Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "entityId"),
        "Record Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "recordName"),
        "Record Name",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "documentTypeName"),
        "Document Type",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "category"),
        "Category",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "filename"),
        "Filename",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "mimeType"),
        "Mime Type",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "azureBlobName"),
        "Azure Blob Name",
        recordFields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "hasExpiry"),
        "Has Expiry",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "expiryDate"),
        "Expiry Date",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        recordFields,
      );
    };

    const parseApprovalDocument = (oldValue, newValue) => {
      addField(
        ...parseField(oldValue, newValue, "id"),
        "Document Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "entityId"),
        "Record Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "recordName"),
        "Record Name",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "checkType"),
        "Check Type",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "filename"),
        "Filename",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "mimeType"),
        "Mime Type",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "azureBlobName"),
        "Azure Blob Name",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        recordFields,
      );
    };

    const parseLink = (oldValue, newValue) => {
      addField(
        ...parseField(oldValue, newValue, "id"),
        "Link Id",
        recordFields,
      );
      addField(...parseField(oldValue, newValue, "url"), "URL", recordFields);
      addField(
        ...parseField(oldValue, newValue, "description"),
        "Description",
        recordFields,
      );
      addField(...parseField(oldValue, newValue, "type"), "Type", recordFields);
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        recordFields,
      );
    };

    const parseCountry = (oldValue, newValue) => {
      addField(
        ...parseField(oldValue, newValue, "id"),
        "Country Id",
        recordFields,
      );
      addField(...parseField(oldValue, newValue, "name"), "Name", recordFields);
      addField(
        ...parseField(oldValue, newValue, "isoCode2"),
        "ISO Code2",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "isoCode3"),
        "ISO Code3",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "region"),
        "Region",
        recordFields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "supportsMdm"),
        "Supports Mdm",
        recordFields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "isJllCountry"),
        "Jll Country",
        recordFields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "illegal"),
        "Illegal",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "mlroContactNames"),
        "MLRO Contact Names",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "mlroContactEmails"),
        "MLRO Contact Emails",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "mlroDelegateNames"),
        "MLRO Delegate Names",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "mlroDelegateEmails"),
        "MLRO Delegate Emails",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        recordFields,
      );
    };

    const parseExchange = (oldValue, newValue) => {
      addField(
        ...parseField(oldValue, newValue, "id"),
        "Exchange Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "countryName"),
        "Country Name",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "exchangeName"),
        "Exchange Name",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "exchangeCode"),
        "Exchange Code",
        recordFields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "isEquivalent"),
        "Is Equivalent",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        recordFields,
      );
    };

    const parseRegulator = (oldValue, newValue) => {
      addField(
        ...parseField(oldValue, newValue, "id"),
        "Regulator Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "countryName"),
        "Country Name",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "regulatorName"),
        "Regulator Name",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "regulatorCode"),
        "Regulator Code",
        recordFields,
      );
      addField(
        ...parseBoolean(oldValue, newValue, "isEquivalent"),
        "Is Equivalent",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        recordFields,
      );
    };

    const parseRelatedEntity = (oldValue, newValue) => {
      addField(
        ...parseField(oldValue, newValue, "id"),
        "Related Entity Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "entityId"),
        "Record Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "recordName"),
        "Record Name",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "relationtype"),
        "Relation Type",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "relatedLocalEntityId"),
        "Related Local Record Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "relatedMdmId"),
        "Related MDM Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "relatedDnbId"),
        "Related D&B Id",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "relatedEntityName"),
        "Related Record Name",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "entityCategory"),
        "Legal Status",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "addressLine1"),
        "Address Line 1",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "addressLine2"),
        "Address Line 2",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "addressLine3"),
        "Address Line 3",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "addressLine4"),
        "Address Line 4",
        recordFields,
      );
      addField(...parseField(oldValue, newValue, "city"), "City", recordFields);
      addField(
        ...parseField(oldValue, newValue, "county"),
        "County",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "stateProvince"),
        "Atate/Province",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "postcode"),
        "Postcode",
        recordFields,
      );
      addField(
        ...parseField(oldValue, newValue, "countryName"),
        "Country",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "createdDate"),
        "Created Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "createdByUserName",
          "createdByUserEmail",
        ),
        "Created By",
        recordFields,
      );
      addField(
        ...parseDate(oldValue, newValue, "lastModifiedDate"),
        "Last Modified Date",
        recordFields,
      );
      addField(
        ...parseUser(
          oldValue,
          newValue,
          "lastModifiedByUserName",
          "lastModifiedByUserEmail",
        ),
        "Last Modified By",
        recordFields,
      );
    };

    const parseClientUser = (oldValue, newValue) => {
      const ov = oldValue
        ? oldValue
        : {
            clientUser: {
              username: "",
              userEmail: "",
            },
          };

      const nv = newValue
        ? newValue
        : {
            clientUser: {
              username: "",
              userEmail: "",
            },
          };
      addField(
        ...parseUser(ov.clientUser, nv.clientUser, "username", "userEmail"),
        "Requested to",
        recordFields,
      );
    };

    const parseDocumentRequestType = (oldValue, newValue, fieldName) => {
      let ov = null;
      if (
        oldValue &&
        oldValue[fieldName] !== null &&
        oldValue[fieldName] !== undefined
      ) {
        ov =
          Number(oldValue[fieldName]) === 2
            ? t(
                "labels.record.documentRequest.steps.requestTypeStep.specificRequest",
              )
            : t(
                "labels.record.documentRequest.steps.requestTypeStep.genericRequest",
              );
        delete oldValue[fieldName];
      }
      let nv = null;
      if (
        newValue &&
        newValue[fieldName] !== null &&
        newValue[fieldName] !== undefined
      ) {
        nv =
          Number(newValue[fieldName]) === 2
            ? t(
                "labels.record.documentRequest.steps.requestTypeStep.specificRequest",
              )
            : t(
                "labels.record.documentRequest.steps.requestTypeStep.genericRequest",
              );
        delete newValue[fieldName];
      }
      return [ov, nv];
    };

    const parseClientRequestType = (oldValue, newValue) => {
      addField(
        ...parseDocumentRequestType(oldValue, newValue, "requestType"),
        "Request Type",
        recordFields,
      );
    };

    const parseRequestedRecordNames = (records = []) => {
      const recordNames = [];
      records.forEach((r) => {
        recordNames.push(
          (
            (r.entity.attributes || []).find((a) => a.id === "3") || {
              value: "",
            }
          ).value,
        );
      });

      return recordNames;
    };

    const parseRequestedEntities = (oldValue, newValue) => {
      const ov =
        oldValue !== undefined &&
        oldValue !== null &&
        Array.isArray(oldValue.records)
          ? oldValue
          : {
              records: [],
            };

      const nv =
        newValue !== undefined &&
        newValue !== null &&
        Array.isArray(newValue.records)
          ? newValue
          : {
              records: [],
            };

      const oldRecords = [];
      const newRecords = [];

      if (nv.records.length > ov.records.length) {
        oldRecords.push(
          ...parseRequestedRecordNames(ov.records).concat(
            new Array(nv.records.length - ov.records.length).fill(""),
          ),
        );
        newRecords.push(...parseRequestedRecordNames(nv.records));
      } else {
        newRecords.push(
          ...parseRequestedRecordNames(nv.records).concat(
            new Array(ov.records.length - nv.records.length).fill(""),
          ),
        );
        oldRecords.push(...parseRequestedRecordNames(ov.records));
      }

      for (let i = 0; i < newRecords.length; i++) {
        addField(
          oldRecords[i],
          newRecords[i],
          i === 0 ? "Documents requested for" : "",
          recordFields,
        );
      }
    };

    const parseClientRequests = (oldValue, newValue) => {
      parseClientUser(oldValue, newValue);
      parseClientRequestType(oldValue, newValue);
      parseRequestedEntities(oldValue, newValue);
    };

    const parseGeneric = (oldValue, newValue) => {
      for (let k in oldValue || {}) {
        addField(...parseField(oldValue, newValue, k), k, recordFields);
      }
      for (let k in newValue || {}) {
        addField(...parseField(null, newValue, k), k, recordFields);
      }
    };

    const parse = (action, recordType, oldValue, newValue) => {
      if (recordType === recordTypes.record.name) {
        if (
          action === recordTypes.record.actions.updateAssociation.name ||
          action === recordTypes.record.actions.removeAssociation.name
        ) {
          parseAssociations(oldValue, newValue);
        } else if (
          action === recordTypes.record.actions.addKycDocument.name ||
          action === recordTypes.record.actions.removeKycDocument.name ||
          action === recordTypes.record.actions.addAmlDocument.name ||
          action === recordTypes.record.actions.removeAmlDocument.name
        ) {
          parseEntityDocument(oldValue, newValue);
        } else if (
          action === recordTypes.record.actions.uploadApprovalDocument
        ) {
          parseApprovalDocument(oldValue, newValue);
        } else if (
          action === recordTypes.record.actions.addAddress.name ||
          action === recordTypes.record.actions.updateAddress.name ||
          action === recordTypes.record.actions.removeAddress.name
        ) {
          parseAddresses(oldValue, newValue);
        } else if (
          action === recordTypes.record.actions.addKycLink.name ||
          action === recordTypes.record.actions.updateKycLink.name ||
          action === recordTypes.record.actions.removeKycLink.name
        ) {
          parseLink(oldValue, newValue);
        } else if (
          action === recordTypes.record.actions.addRelatedEntity.name ||
          action === recordTypes.record.actions.removeRelatedEntity.name
        ) {
          parseRelatedEntity(oldValue, newValue);
        } else if (
          action === recordTypes.record.actions.finalizeKycScore.name ||
          action === recordTypes.record.actions.finalizeAmlScore.name ||
          action === processActions.approveScore ||
          action === processActions.rejectScore ||
          action === processActions.sendBackScore ||
          action === recordTypes.record.actions.expireScore.name
        ) {
          parseScore(oldValue, newValue);
        } else if (action === recordTypes.record.actions.updateScores.name) {
          parseScores(oldValue, newValue);
        } else if (
          action === recordTypes.record.actions.requestClientDocuments.name
        ) {
          parseClientRequests(oldValue, newValue);
        } else {
          parseEntity(oldValue, newValue);
        }
      } else if (recordType === recordTypes.checkRequest.name) {
        parseCheckRequest(oldValue, newValue);
      } else if (recordType === recordTypes.country.name) {
        parseCountry(oldValue, newValue);
      } else if (recordType === recordTypes.countryExchange.name) {
        parseExchange(oldValue, newValue);
      } else if (recordType === recordTypes.countryRegulator.name) {
        parseRegulator(oldValue, newValue);
      } else {
        parseGeneric(oldValue, newValue);
      }
    };

    const {
      action,
      record_type: recordType,
      old_data: oldData,
      new_data: newData,
    } = record || {};
    const oldDataObj = oldData ? JSON.parse(oldData) : {};
    const newDataObj = newData ? JSON.parse(newData) : {};
    parse(action, recordType, oldDataObj, newDataObj);

    setRecordFields(recordFields);
    setAttributes(attributes);
    setCheckRequests(checkRequests);
    setScores(scores);
    setAddresses(addresses);
    setAssociations(associations);
  }, [record]);

  const renderValuesTable = (header, values, key) => {
    if (!values || values.length === 0) {
      return null;
    }
    return (
      <Table className={classes.table} size="small" key={key}>
        <TableHead>
          {header ? (
            <TableRow>
              <TableCell
                align="center"
                colSpan={3}
                className={classes.tableHeader}
              >
                {header}
              </TableCell>
            </TableRow>
          ) : null}

          <TableRow>
            <TableCell
              align="left"
              width="20%"
              className={classes.columnHeader}
            >
              {t("labels.audit.fieldName")}
            </TableCell>
            <TableCell
              align="center"
              width="40%"
              className={classes.columnHeader}
            >
              {t("labels.audit.oldValue")}
            </TableCell>
            <TableCell
              align="center"
              width="40%"
              className={classes.columnHeader}
            >
              {t("labels.audit.newValue")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {values.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell className={classes.fieldName}>{row.name}</TableCell>
              <TableCell
                align="center"
                className={
                  row.oldValue === row.newValue
                    ? classes.fieldValue
                    : classes.fieldValueChanged
                }
              >
                {row.oldValue}
              </TableCell>
              <TableCell
                align="center"
                className={
                  row.oldValue === row.newValue
                    ? classes.fieldValue
                    : classes.fieldValueChanged
                }
              >
                {row.newValue}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderAttributeValuesTable = () => {
    if (!attributes || attributes.length === 0) {
      return null;
    }
    return (
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              colSpan={5}
              className={classes.tableHeader}
            >
              <Typography variant="h6">
                {t("labels.audit.attributes")}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              align="left"
              width="20%"
              rowSpan={2}
              className={classes.columnHeader}
            >
              {t("labels.audit.attributeName")}
            </TableCell>
            <TableCell
              align="center"
              width="40%"
              colSpan={2}
              className={classes.columnHeader}
            >
              {t("labels.audit.oldValue")}
            </TableCell>
            <TableCell
              align="center"
              width="40%"
              colSpan={2}
              className={classes.columnHeader}
            >
              {t("labels.audit.newValue")}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              align="center"
              width="20%"
              className={classes.columnHeader}
            >
              {t("labels.audit.sourceValue")}
            </TableCell>
            <TableCell
              align="center"
              width="20%"
              className={classes.columnHeader}
            >
              {t("labels.audit.userValue")}
            </TableCell>
            <TableCell
              align="center"
              width="20%"
              className={classes.columnHeader}
            >
              {t("labels.audit.sourceValue")}
            </TableCell>
            <TableCell
              align="center"
              width="20%"
              className={classes.columnHeader}
            >
              {t("labels.audit.userValue")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attributes.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell className={classes.fieldName}>{row.name}</TableCell>
              <TableCell
                align="center"
                className={
                  row.oldSourceValue === row.newSourceValue
                    ? classes.fieldValue
                    : classes.fieldValueChanged
                }
              >
                {row.oldSourceValue}
              </TableCell>
              <TableCell
                align="center"
                className={
                  row.oldUserValue === row.newUserValue
                    ? classes.fieldValue
                    : classes.fieldValueChanged
                }
              >
                {row.oldUserValue}
              </TableCell>
              <TableCell
                align="center"
                className={
                  row.oldSourceValue === row.newSourceValue
                    ? classes.fieldValue
                    : classes.fieldValueChanged
                }
              >
                {row.newSourceValue}
              </TableCell>
              <TableCell
                align="center"
                className={
                  row.oldUserValue === row.newUserValue
                    ? classes.fieldValue
                    : classes.fieldValueChanged
                }
              >
                {row.newUserValue}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        {renderValuesTable("", recordFields)}
        {renderAttributeValuesTable()}
        {renderValuesTable(t("labels.audit.kycCheck"), checkRequests.KYC)}
        {renderValuesTable(t("labels.audit.amlCheck"), checkRequests.AML)}
        {renderValuesTable(t("labels.audit.kycScore"), scores.KYC)}
        {renderValuesTable(t("labels.audit.amlScore"), scores.AML)}

        {(addresses || []).map((a, idx) =>
          renderValuesTable(t("labels.audit.address"), a, idx),
        )}

        {(associations || []).map((a, idx) =>
          renderValuesTable(t("labels.audit.association"), a, idx),
        )}
      </Paper>
    </div>
  );
};

export default withStyles(styles, { withTheme: true })(ViewRecord);
